import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const ecosystemNodes = [
  { id: 'hg-estates', name: 'HG Estates', position: { x: 50, y: 12 }, category: 'Land' },
  { id: 'kritam', name: 'Kritam', position: { x: 18, y: 35 }, category: 'Design' },
  { id: 'horizon', name: 'Horizon', position: { x: 82, y: 35 }, category: 'Materials' },
  { id: 'haveli', name: 'Haveli', position: { x: 30, y: 58 }, category: 'Build' },
  { id: 'hans', name: 'HANS', position: { x: 70, y: 58 }, category: 'Maintain' },
  { id: 'hifazat', name: 'Hifazat', position: { x: 12, y: 78 }, category: 'Secure' },
  { id: 'hemraj-hotels', name: 'Hemraj Hotels', position: { x: 50, y: 90 }, category: 'Experience' },
  { id: 'horizon-app', name: 'Horizon App', position: { x: 88, y: 78 }, category: 'Technology' },
];

const connections = [
  ['hg-estates', 'kritam'],
  ['hg-estates', 'horizon'],
  ['kritam', 'haveli'],
  ['horizon', 'haveli'],
  ['horizon', 'hans'],
  ['haveli', 'hans'],
  ['haveli', 'hifazat'],
  ['hans', 'hemraj-hotels'],
  ['hans', 'horizon-app'],
  ['hifazat', 'hemraj-hotels'],
  ['hemraj-hotels', 'horizon-app'],
];

export const Ecosystem = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const getNodePosition = (id) => {
    const node = ecosystemNodes.find(n => n.id === id);
    return node ? node.position : { x: 50, y: 50 };
  };

  return (
    <section
      id="ecosystem"
      data-testid="ecosystem-section"
      ref={ref}
      className="py-24 lg:py-32 bg-[#EFEBE4]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className="text-center mb-16 lg:mb-24">
          <motion.p
            className="overline mb-4"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            The Ecosystem
          </motion.p>

          <motion.h2
            data-testid="ecosystem-title"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-[#1A1A1A] max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            A Connected System Where Every Layer Works Together
          </motion.h2>

          <motion.p
            className="mt-6 text-base font-medium text-[#4A4A4A] max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            A typical Hemraj project flows through multiple companies, each adding value 
            while maintaining consistency and quality throughout the journey.
          </motion.p>
        </div>

        {/* Ecosystem Visualization */}
        <motion.div
          data-testid="ecosystem-visualization"
          className="relative aspect-[4/3] lg:aspect-[16/9] max-w-5xl mx-auto"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          {/* Animated background glow */}
          <div className="absolute inset-0 overflow-hidden">
            <motion.div 
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-[#C5A880]/10 rounded-full blur-3xl"
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.3, 0.5, 0.3]
              }}
              transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            />
          </div>

          {/* SVG for connections with animated flow */}
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#C5A880" stopOpacity="0.2" />
                <stop offset="50%" stopColor="#C5A880" stopOpacity="0.8" />
                <stop offset="100%" stopColor="#C5A880" stopOpacity="0.2" />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="0.5" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            {connections.map(([from, to], index) => {
              const fromPos = getNodePosition(from);
              const toPos = getNodePosition(to);
              return (
                <g key={`${from}-${to}`}>
                  {/* Base line */}
                  <motion.line
                    x1={fromPos.x}
                    y1={fromPos.y}
                    x2={toPos.x}
                    y2={toPos.y}
                    stroke="#C5A880"
                    strokeWidth="0.15"
                    strokeOpacity="0.3"
                    initial={{ pathLength: 0 }}
                    animate={isInView ? { pathLength: 1 } : {}}
                    transition={{ duration: 1.5, delay: 0.5 + index * 0.1 }}
                  />
                  {/* Animated flowing particle */}
                  <motion.circle
                    r="0.8"
                    fill="#C5A880"
                    filter="url(#glow)"
                    initial={{ opacity: 0 }}
                    animate={isInView ? {
                      opacity: [0, 1, 1, 0],
                      cx: [fromPos.x, toPos.x],
                      cy: [fromPos.y, toPos.y],
                    } : {}}
                    transition={{
                      duration: 2,
                      delay: 1.5 + index * 0.3,
                      repeat: Infinity,
                      repeatDelay: 3,
                      ease: "easeInOut"
                    }}
                  />
                </g>
              );
            })}
          </svg>

          {/* Nodes */}
          {ecosystemNodes.map((node, index) => (
            <motion.div
              key={node.id}
              data-testid={`ecosystem-node-${node.id}`}
              className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group"
              style={{ left: `${node.position.x}%`, top: `${node.position.y}%` }}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={isInView ? { opacity: 1, scale: 1 } : {}}
              transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
              whileHover={{ scale: 1.15 }}
            >
              <div className="relative">
                {/* Outer glow ring */}
                <motion.div 
                  className="absolute inset-0 bg-[#C5A880]/20 blur-md"
                  style={{ width: '100%', height: '100%' }}
                  animate={{ 
                    scale: [1, 1.3, 1],
                    opacity: [0.3, 0.6, 0.3]
                  }}
                  transition={{ 
                    duration: 3, 
                    repeat: Infinity, 
                    ease: "easeInOut",
                    delay: index * 0.2
                  }}
                />
                {/* Node box - bigger */}
                <div className="relative w-20 h-20 lg:w-28 lg:h-28 bg-[#1A1A1A] flex items-center justify-center transition-all duration-500 group-hover:bg-[#C5A880] shadow-lg group-hover:shadow-[#C5A880]/30 group-hover:shadow-xl">
                  <span className="text-[#F9F6F0] font-heading text-xs lg:text-sm font-medium text-center px-2 leading-tight group-hover:text-[#1A1A1A]">
                    {node.name}
                  </span>
                </div>
                
                {/* Category label */}
                <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 whitespace-nowrap">
                  <span className="text-[10px] lg:text-xs uppercase tracking-widest text-[#4A4A4A] font-medium">
                    {node.category}
                  </span>
                </div>
              </div>
            </motion.div>
          ))}

          {/* Center label with rotating border */}
          <motion.div
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 text-center pointer-events-none"
            initial={{ opacity: 0 }}
            animate={isInView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 1.5 }}
          >
            <div className="relative w-28 h-28 lg:w-36 lg:h-36">
              {/* Rotating outer ring */}
              <motion.div 
                className="absolute inset-0 border-2 border-[#C5A880]/30"
                animate={{ rotate: 360 }}
                transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                style={{ borderRadius: '0' }}
              />
              {/* Inner content */}
              <div className="absolute inset-2 border border-[#C5A880]/20 flex items-center justify-center bg-[#EFEBE4]/50 backdrop-blur-sm">
                <div className="text-center">
                  <span className="font-heading text-lg lg:text-xl text-[#C5A880] tracking-wider font-medium">HG</span>
                  <p className="text-[8px] lg:text-[10px] text-[#4A4A4A] mt-1 uppercase tracking-widest">Ecosystem</p>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Flow Description */}
        <motion.div
          className="mt-16 lg:mt-24 grid grid-cols-1 md:grid-cols-3 gap-8"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <div className="text-center">
            <p className="font-heading text-2xl font-light text-[#1A1A1A]">Acquire</p>
            <p className="text-sm text-[#4A4A4A] mt-2">Land acquired by HG Estates</p>
          </div>
          <div className="text-center">
            <p className="font-heading text-2xl font-light text-[#1A1A1A]">Create</p>
            <p className="text-sm text-[#4A4A4A] mt-2">Designed, built & supplied in-house</p>
          </div>
          <div className="text-center">
            <p className="font-heading text-2xl font-light text-[#1A1A1A]">Experience</p>
            <p className="text-sm text-[#4A4A4A] mt-2">Operated & experienced through our brands</p>
          </div>
        </motion.div>

        {/* Lifestyle Showcase */}
        <motion.div
          className="mt-16 lg:mt-24 grid grid-cols-1 md:grid-cols-3 gap-4"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 1 }}
        >
          <div className="image-zoom aspect-[4/3]">
            <img 
              src="https://images.unsplash.com/photo-1759902918744-dcc0cd25dba5?w=600&q=75&fm=webp&fit=crop"
              alt="Cattle grazing in green pastures"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="image-zoom aspect-[4/3]">
            <img 
              src="https://images.pexels.com/photos/36675935/pexels-photo-36675935.jpeg?auto=compress&cs=tinysrgb&w=600&h=450"
              alt="Beautiful garden with pond"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div className="image-zoom aspect-[4/3]">
            <img 
              src="https://images.unsplash.com/photo-1769414703397-e345d715892b?w=600&q=75&fm=webp&fit=crop"
              alt="Cultural evening with lanterns"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </motion.div>
        
        <motion.p
          className="mt-8 text-center text-base font-medium text-[#4A4A4A] max-w-2xl mx-auto"
          initial={{ opacity: 0 }}
          animate={isInView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 1.2 }}
        >
          Sustainable farmlands with gardens, ponds, cattle farming — a peaceful lifestyle with 
          cultural evenings, movie nights, and meaningful connections.
        </motion.p>
      </div>
    </section>
  );
};
